// Member add / update / remove for the Part-uses tab. Extracted so
// `useManufacturingMaterialUsesTab.ts` stays under the file-size hard cap.
import { useCallback } from "react";
import {
  deleteManufacturingMaterialUseMember,
  updateManufacturingMaterialUseMember,
  upsertManufacturingMaterialUseMember,
} from "@/lib/manufacturing/materialUsesClient";
import {
  clampMaterialUsePreferenceRank,
  nextMaterialUsePreferenceRank,
} from "@/lib/manufacturing/materialUsePreferenceRank";
import type {
  MfgMaterialUseMember,
  MfgMaterialUseResolveMode,
} from "@/lib/manufacturing/types";

type Opts = {
  selectedId: string | null;
  members: ReadonlyArray<MfgMaterialUseMember>;
  editResolveMode: MfgMaterialUseResolveMode;
  reloadDetail: (id: string) => Promise<void>;
  setSaveBusy: (busy: boolean) => void;
  setLocalError: (error: string | null) => void;
};

export function useMaterialUseMemberActions({
  selectedId,
  members,
  editResolveMode,
  reloadDetail,
  setSaveBusy,
  setLocalError,
}: Opts) {
  const addMember = useCallback(
    async (materialId: string) => {
      if (!selectedId) return { ok: false as const, error: "Select a use first." };
      setSaveBusy(true);
      setLocalError(null);
      const isFinish = editResolveMode === "finish_variant";
      const r = await upsertManufacturingMaterialUseMember({
        use_id: selectedId,
        material_id: materialId,
        preference_rank: nextMaterialUsePreferenceRank(members),
        is_primary: isFinish && members.filter((m) => m.is_active).length === 0,
      });
      setSaveBusy(false);
      if (!r.ok) {
        setLocalError(r.error);
        return { ok: false as const, error: r.error };
      }
      await reloadDetail(selectedId);
      return { ok: true as const };
    },
    [editResolveMode, members, reloadDetail, selectedId, setLocalError, setSaveBusy],
  );

  const addMembers = useCallback(
    async (materialIds: ReadonlyArray<string>) => {
      if (!selectedId) return { ok: false as const, error: "Select a use first." };
      const ids = materialIds.filter(Boolean);
      if (ids.length === 0) return { ok: true as const, added: 0 };
      setSaveBusy(true);
      setLocalError(null);
      const isFinish = editResolveMode === "finish_variant";
      let activeCount = members.filter((m) => m.is_active).length;
      const rankSeed = members.map((m) => ({
        preference_rank: m.preference_rank,
        is_active: m.is_active,
      }));
      let added = 0;
      let lastError: string | null = null;
      for (const materialId of ids) {
        const preference_rank = nextMaterialUsePreferenceRank(rankSeed);
        const r = await upsertManufacturingMaterialUseMember({
          use_id: selectedId,
          material_id: materialId,
          preference_rank,
          is_primary: isFinish && activeCount === 0,
        });
        if (!r.ok) {
          lastError = r.error;
          break;
        }
        added += 1;
        activeCount += 1;
        rankSeed.push({ preference_rank, is_active: true });
      }
      setSaveBusy(false);
      if (selectedId) await reloadDetail(selectedId);
      if (lastError) {
        setLocalError(added > 0 ? `Added ${added}, then failed: ${lastError}` : lastError);
        return { ok: false as const, error: lastError, added };
      }
      return { ok: true as const, added };
    },
    [editResolveMode, members, reloadDetail, selectedId, setLocalError, setSaveBusy],
  );

  const updateMember = useCallback(
    async (
      memberId: string,
      patch: {
        qty_per_use?: number;
        preference_rank?: number;
        is_active?: boolean;
        is_primary?: boolean;
        finish_code?: string | null;
      },
    ) => {
      setSaveBusy(true);
      setLocalError(null);
      const r = await updateManufacturingMaterialUseMember({
        member_id: memberId,
        ...patch,
        ...(typeof patch.preference_rank === "number"
          ? { preference_rank: clampMaterialUsePreferenceRank(patch.preference_rank) }
          : {}),
      });
      setSaveBusy(false);
      if (!r.ok) {
        setLocalError(r.error);
        return { ok: false as const, error: r.error };
      }
      if (selectedId) await reloadDetail(selectedId);
      return { ok: true as const };
    },
    [reloadDetail, selectedId, setLocalError, setSaveBusy],
  );

  const removeMember = useCallback(
    async (memberId: string) => {
      setSaveBusy(true);
      setLocalError(null);
      const r = await deleteManufacturingMaterialUseMember({ member_id: memberId });
      setSaveBusy(false);
      if (!r.ok) {
        setLocalError(r.error);
        return { ok: false as const, error: r.error };
      }
      if (selectedId) await reloadDetail(selectedId);
      return { ok: true as const };
    },
    [reloadDetail, selectedId, setLocalError, setSaveBusy],
  );

  return { addMember, addMembers, updateMember, removeMember };
}
